"use client"

import { useCallback, useRef, useState } from "react"
import { useTranslations } from "next-intl"
import { toast } from "sonner"
import { useProductCogs } from "@/hooks/use-product-cogs"
import type { CogsOverrideRef } from "@/lib/shopify/economics/cogs-edit-scope"
import { productRowKey } from "./economics-calculations"
import type { ConfigEconomicsItem } from "./types"

interface UseRowCogsEditOptions {
  integrationId: string
  /** Current rows; a queued commit applies the latest plan of its row. */
  rows: ReadonlyArray<ConfigEconomicsItem>
  revalidate: () => Promise<void>
}

/**
 * Inline "this SKU only" COGS edits. A commit follows the row's server-built plan; the new
 * cost shows from `pendingCogs` (keyed by row) until the revalidated rows arrive. Commits on
 * one row run in order, so a clear right after a save removes what the save wrote.
 */
export function useRowCogsEdit({ integrationId, rows, revalidate }: UseRowCogsEditOptions) {
  const tUi = useTranslations("Shopify.ShopifyEconomicsConfigPerformanceView")
  const cogsApi = useProductCogs({ integrationId })
  const [pendingCogs, setPendingCogs] = useState<Record<string, number>>({})

  const latest = useRef({ rows, revalidate, cogsApi, tUi })
  latest.current = { rows, revalidate, cogsApi, tUi }
  const queues = useRef(new Map<string, Promise<void>>())

  const commit = useCallback((row: ConfigEconomicsItem, value: number | null): Promise<void> => {
    const key = productRowKey(row)
    if (value != null) setPendingCogs((prev) => ({ ...prev, [key]: value }))

    const run = async () => {
      const { rows, revalidate, cogsApi, tUi } = latest.current
      try {
        await writeCogs(rows.find((r) => productRowKey(r) === key) ?? row, value, cogsApi)
      } catch {
        toast.error(tUi("cogsSaveFailed"))
      }
      await revalidate()
      setPendingCogs((prev) => {
        if (prev[key] !== value) return prev
        const { [key]: _settled, ...rest } = prev
        return rest
      })
    }

    const next = (queues.current.get(key) ?? Promise.resolve()).then(run)
    queues.current.set(key, next)
    return next
  }, [])

  return { pendingCogs, commit }
}

/** A save writes the plan's targets, then drops the overrides they replace; a clear drops the row's own. */
async function writeCogs(
  row: ConfigEconomicsItem,
  value: number | null,
  { updateCogs, deleteCogs }: ReturnType<typeof useProductCogs>,
): Promise<void> {
  const plan = row.cogs_edit
  if (!plan) throw new Error("This row's COGS is read-only")
  const deleteAll = (refs: ReadonlyArray<CogsOverrideRef>) =>
    Promise.all(refs.map((ref) => deleteCogs(ref.product_id, ref.variant_id)))

  if (value == null) {
    await deleteAll(plan.own)
    return
  }
  await updateCogs(
    plan.upsert.map((target) => ({
      ...target,
      cogs_per_unit: value,
      title: row.title,
      sku: row.sku,
      variant_title: target.variant_id != null ? row.variant_title : null,
    })),
  )
  await deleteAll(plan.removeOnSave)
}
